import React from "react";
import { Box, Typography } from "@mui/material";
import { tokens } from "../../theme";

/**
 * Piezas compartidas de formulario. Todas las pantallas de carga usan estas,
 * así un campo se ve y se comporta igual en cliente, caja, orden o pago.
 */

/** Tarjeta que agrupa un bloque de campos. */
export const FormCard = ({ title, subtitle, action, children, sx }) => (
  <Box
    sx={{
      backgroundColor: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: `${tokens.radiusCard}px`,
      padding: { xs: "16px", md: "18px 20px 20px" },
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      minWidth: 0,
      ...sx,
    }}
  >
    {(title || action) && (
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", flexGrow: 1, minWidth: 0 }}>
          {title && <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>{title}</Typography>}
          {subtitle && (
            <Typography sx={{ fontSize: "12px", color: tokens.ink3, lineHeight: 1.45 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

/** Grilla de campos: una columna en teléfono, las que pidas en escritorio. */
export const FormGrid = ({ columns = 2, children, sx }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        md: `repeat(${columns}, minmax(0, 1fr))`,
      },
      gap: { xs: "14px", md: "16px" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

/** Etiqueta + control + ayuda o error. El error gana sobre la ayuda. */
export const Field = ({ label, hint, error, required, action, children, span, sx }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      minWidth: 0,
      gridColumn: span ? { md: `span ${span}` } : undefined,
      ...sx,
    }}
  >
    {label && (
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
        <Typography
          component="label"
          sx={{ fontSize: "12.5px", fontWeight: 500, color: tokens.ink2, flexGrow: 1 }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: tokens.brandDark, marginLeft: "3px" }}>
              *
            </Box>
          )}
        </Typography>
        {action}
      </Box>
    )}

    {children}

    {(error || hint) && (
      <Typography
        sx={{
          fontSize: "11.5px",
          lineHeight: 1.4,
          color: error ? tokens.brandDark : tokens.ink3,
        }}
      >
        {error || hint}
      </Typography>
    )}
  </Box>
);

const controlBase = (invalid) => ({
  width: "100%",
  minHeight: { xs: 44, md: 40 },
  padding: "0 12px",
  fontFamily: "inherit",
  fontSize: { xs: "15px", md: "14px" },
  color: tokens.ink,
  backgroundColor: tokens.surface,
  border: `1px solid ${invalid ? tokens.brand : tokens.border}`,
  borderRadius: `${tokens.radiusControl}px`,
  transition: "border-color 120ms ease, box-shadow 120ms ease",
  "&::placeholder": { color: tokens.ink4 },
  "&:hover:not(:disabled)": { borderColor: tokens.borderStrong },
  "&:focus, &:focus-visible": {
    outline: "none",
    borderColor: tokens.brand,
    boxShadow: `0 0 0 3px ${tokens.brandTint}`,
  },
  "&:disabled": {
    backgroundColor: tokens.surface2,
    color: tokens.ink3,
    cursor: "not-allowed",
  },
});

/** Input de texto o número. Los números salen con cifras tabulares. */
export const TextInput = React.forwardRef(({ invalid, numeric, sx, ...rest }, ref) => (
  <Box
    component="input"
    ref={ref}
    sx={{
      ...controlBase(invalid),
      fontVariantNumeric: numeric ? "tabular-nums" : "normal",
      ...sx,
    }}
    {...rest}
  />
));

/** Área de texto con el mismo aspecto que el input. */
export const TextArea = React.forwardRef(({ invalid, rows = 4, sx, ...rest }, ref) => (
  <Box
    component="textarea"
    ref={ref}
    rows={rows}
    sx={{
      ...controlBase(invalid),
      minHeight: "auto",
      padding: "10px 12px",
      lineHeight: 1.5,
      resize: "vertical",
      ...sx,
    }}
    {...rest}
  />
));

/** Select nativo: en teléfono abre el selector del sistema, que es lo mejor. */
export const SelectInput = React.forwardRef(({ invalid, children, sx, ...rest }, ref) => (
  <Box
    component="select"
    ref={ref}
    sx={{
      ...controlBase(invalid),
      appearance: "none",
      cursor: "pointer",
      paddingRight: "34px",
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23615d5a' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 12px center",
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
));

/**
 * Fila de acciones al pie del formulario. En teléfono se pega abajo para que
 * el botón principal quede siempre al alcance del pulgar.
 */
export const FormActions = ({ children, sticky = true, sx }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      flexWrap: "wrap",
      position: sticky ? { xs: "sticky", md: "static" } : "static",
      bottom: sticky ? 0 : "auto",
      zIndex: sticky ? 5 : "auto",
      backgroundColor: tokens.canvas,
      paddingTop: "12px",
      paddingBottom: { xs: "12px", md: 0 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

export default {
  FormCard,
  FormGrid,
  Field,
  TextInput,
  TextArea,
  SelectInput,
  FormActions,
};
